// React
import React, { useState } from 'react';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Mantine
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
  MRT_RowSelectionState,
} from 'mantine-react-table';
import { ActionIcon, Menu } from '@mantine/core';

import {
  IconDotsVertical,
  IconWand,
  IconArrowBackUpDouble,
  IconCloudUp,
  IconEyeDown,
  IconCheck,
  IconDownload,
} from '@tabler/icons-react';

// Custom Components
import { TableProgressBar } from 'components/common';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { SubmissionTopToolbar } from './components/SubmissionTopToolbar';

// API
import { useUpdateSubmission } from 'api/resource';

// Interfaces
import { Submission, SubmissionStatus, PayorEnum, Payor } from 'interfaces';

// Columns
import { SubmissionColumns } from 'columns/Submission';

import { nowISO } from 'utils/dateUtils';

import { useQueryClient } from '@tanstack/react-query';
import { useRevertSubmission } from 'api/resource';
import {
  useGenerateSubmissionFile,
  useSubmitToHealthAuthority,
} from 'api/action';
import { ConfirmDialog } from 'components/common/ConfirmDialog';
import { ViewFileModal } from 'components/common/ViewFileModal';

import { useCanModify } from 'hooks/useCanModify';
import { useDownloadFromS3 } from 'hooks';
interface SubmissionManagementProps {
  submissions: Submission[];
  payors: Payor[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: string;
}

const BUCKET = `submission-files-${import.meta.env.VITE_ACCOUNT_ID}`;

export const SubmissionManagement: React.FC<SubmissionManagementProps> = ({
  submissions,
  payors,
  isLoading,
  isError,
  isFetching,
  errorMessage,
}) => {
  const title = 'Submissions Management';
  useDocumentTitle('Submissions | QBilling');
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState<boolean>(false);
  const [revertOpen, setRevertOpen] = useState<boolean>(false);
  const [submitOpen, setSubmitOpen] = useState<boolean>(false);
  const [viewP1, setViewP1] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<
    SubmissionStatus | undefined
  >(undefined);
  const updateSubmission = useUpdateSubmission();
  const revertSubmission = useRevertSubmission();
  const regenerateSubmission = useGenerateSubmissionFile();
  const submitToHealth = useSubmitToHealthAuthority();
  const queryClient = useQueryClient();
  const { canModify } = useCanModify('submissions');

  const handleRevertSubmission = async (submission: Submission) => {
    await revertSubmission.mutateAsync(submission);
    queryClient.invalidateQueries();
    setRevertOpen(false);
  };

  const handleRegenerateSubmission = async (submission: Submission) => {
    await regenerateSubmission.mutateAsync(submission.id);
    await updateSubmission.mutate({
      ...submission,
      status: SubmissionStatus.generating,
    });
    setRegenerateOpen(false);
  };

  const handleSubmitSubmission = async (submission: Submission) => {
    await submitToHealth.mutateAsync({
      submissionID: submission.id,
      payor: PayorEnum.mb,
    });
    setSubmitOpen(false);
  };

  const handleSetSubmissionUploaded = () => {
    const updateData: Submission = {
      ...selectedSubmission,
      status: SubmissionStatus.submitted,
      uploaded: true,
      submittedAt: nowISO(),
    };
    updateSubmission.mutate(updateData);
  };

  const columns: MRT_ColumnDef<Submission>[] = SubmissionColumns();

  const DownloadMenuItem: React.FC<{
    filename: string;
    disabled?: boolean;
  }> = ({ filename, disabled = false }) => {
    const [loading, setLoading] = useState(false);
    const download = useDownloadFromS3(BUCKET, filename, setLoading);
    return (
      <Menu.Item
        leftSection={<IconDownload size={14} />}
        disabled={!filename || disabled || loading}
        onClick={() => download()}
      >
        {loading ? 'Downloading…' : 'Download Submission'}
      </Menu.Item>
    );
  };

  const tableOptions: MRT_TableOptions<Submission> = {
    data:
      filterStatus === undefined
        ? submissions
        : submissions.filter(
            (submission) => submission.status === filterStatus
          ),
    columns,
    state: {
      isLoading: isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      showLoadingOverlay: isFetching || isLoading,
      columnPinning: { left: ['mrt-row-actions'] },
      columnSizing: { 'mrt-row-actions': 36 },
      rowSelection: rowSelection,
    },
    enableRowSelection: true,
    enableColumnPinning: true,
    enableColumnResizing: false,
    enableMultiRowSelection: false,
    enableToolbarInternalActions: true,
    positionActionsColumn: 'first',
    positionToolbarAlertBanner: 'none',
    enableGlobalFilterModes: true,
    mantineSearchTextInputProps: {
      placeholder: 'Search Submissions',
    },
    onRowSelectionChange: setRowSelection,
    renderTopToolbarCustomActions: () =>
      SubmissionTopToolbar({
        submissions,
        filterStatus,
        setFilterStatus,
      }),
    mantineToolbarAlertBannerProps: () => {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 36,
        minSize: 36,
        maxSize: 36,
        Cell: ({ row }: { row: MRT_Row<Submission> }) => {
          const payor = payors.find((p) => p?.id === row?.original?.payorID);
          const filename =
            payor?.name === 'MB'
              ? row.original.filename
              : row.original.filename;
          return (
            <Menu shadow="md" width={220} withinPortal>
              <Menu.Target>
                <ActionIcon
                  data-testid="submission-row-actions"
                  variant="transparent"
                  aria-label="Submission Actions"
                  onClick={() => {
                    setRowSelection({ [row.id]: true });
                    setSelectedSubmission(row.original);
                  }}
                >
                  <IconDotsVertical stroke={1.5} size={22} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Downloads</Menu.Label>
                <DownloadMenuItem filename={filename} />
                <Menu.Label>Safe Zone</Menu.Label>
                <Menu.Item
                  data-testid="regenerate-menu-item"
                  leftSection={<IconWand size={14} />}
                  onClick={() => setRegenerateOpen(true)}
                  disabled={
                    row.original.status !== SubmissionStatus.error || !canModify
                  }
                >
                  Regenerate
                </Menu.Item>
                <Menu.Item
                  data-testid="submit-menu-item"
                  leftSection={<IconCloudUp size={14} />}
                  disabled={
                    row.original.status !== SubmissionStatus.created ||
                    !canModify
                  }
                  onClick={() => setSubmitOpen(true)}
                >
                  Submit
                </Menu.Item>
                <Menu.Item
                  data-testid="view-p1-menu-item"
                  leftSection={<IconEyeDown size={14} />}
                  disabled={!row.original.p1File}
                  onClick={() => setViewP1(true)}
                >
                  View P1 Report
                </Menu.Item>
                <Menu.Item
                  data-testid="mark-submitted-menu-item"
                  leftSection={<IconCheck size={14} />}
                  disabled={
                    row.original.status !== SubmissionStatus.created ||
                    !canModify
                  }
                  onClick={() => handleSetSubmissionUploaded()}
                >
                  Mark Submitted
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Danger Zone</Menu.Label>
                <Menu.Item
                  data-testid="revert-menu-item"
                  color="red"
                  onClick={() => setRevertOpen(true)}
                  leftSection={<IconArrowBackUpDouble size={14} />}
                  disabled={
                    row.original.status === SubmissionStatus.submitted ||
                    row.original.status === SubmissionStatus.submitting ||
                    row.original.status === SubmissionStatus.reverted ||
                    !canModify
                  }
                >
                  Revert
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          );
        },
      },
    },
  };

  return (
    <>
      <ViewFileModal
        modalOpen={viewP1}
        setModalOpen={setViewP1}
        filename={selectedSubmission?.p1File}
        bucket={`submission-files-${import.meta.env.VITE_ACCOUNT_ID}`}
        title={`${selectedSubmission?.filename} P1 Report`}
      />

      <ConfirmDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onConfirm={() => {
          handleSubmitSubmission(selectedSubmission);
        }}
        isLoading={submitToHealth?.isPending}
        title={`Send Submission`}
        body="Are you sure you want to send this submission to the health authority?"
        confirmText="Send"
      />
      <ConfirmDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onConfirm={() => {
          handleRegenerateSubmission(selectedSubmission);
        }}
        isLoading={regenerateSubmission?.isPending}
        title="Regenerate Submission"
        body="Are you sure you want to regenerate this submission?"
        confirmText="Regenerate"
      />
      <ConfirmDialog
        open={revertOpen}
        onClose={() => setRevertOpen(false)}
        onConfirm={() => {
          handleRevertSubmission(selectedSubmission);
        }}
        isLoading={revertSubmission?.isPending}
        title="Revert Submission"
        body="Are you sure you want to revert this submission?"
        confirmText="Revert"
      />
      <BillingDataGridWrapper
        title={title}
        dataTestID="submissionManagement"
        tableOptions={tableOptions}
      />
    </>
  );
};
